import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Phone, WhatsappLogo, User, Clock, CheckCircle, HourglassHigh, CircleDashed, CaretRight, Camera, ChatText, Wrench, Desktop, FileXls, ClockCounterClockwise, ArrowsClockwise, PencilSimple, FilePdf, Cpu, Tag, X, CurrencyDollar, Shield, Warning, Bell, Crown, Trash, CalendarBlank, Funnel, Buildings, UserCirclePlus, Share, NotePencil } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CompanyCombobox } from "@/components/ui/CompanyCombobox";
import ProductSearchInput from "@/components/products/ProductSearchInput";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status, statusTypes = [] }) => {
  // Find status in statusTypes or use default
  const statusInfo = statusTypes.find(s => s.name === status);
  
  const defaultConfig = {
    nuevo: { label: "Nuevo", icon: CircleDashed, color: "#3B82F6" },
    en_proceso: { label: "En Proceso", icon: HourglassHigh, color: "#F59E0B" },
    resuelto: { label: "Resuelto", icon: CheckCircle, color: "#10B981" },
    no_solucionado: { label: "No Solucionado", icon: X, color: "#EF4444" },
    otro: { label: "Otro", icon: Tag, color: "#6366F1" }
  };
  
  const config = defaultConfig[status] || { label: status, icon: Tag, color: "#64748B" };
  const label = statusInfo?.label || config.label;
  const color = statusInfo?.color || config.color;
  const Icon = config.icon;
  
  return (
    <span 
      className="status-badge"
      style={{ 
        backgroundColor: `${color}20`, 
        color: color, 
        borderColor: `${color}50` 
      }}
      data-testid={`status-badge-${status}`}
    >
      <Icon size={14} weight="duotone" className="mr-1" />
      {label}
    </span>
  );
};

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const config = {
    critico: { label: "Critico", color: "#EF4444", bg: "bg-red-50 border-red-200" },
    alto: { label: "Alto", color: "#F97316", bg: "bg-orange-50 border-orange-200" },
    normal: { label: "Normal", color: "#3B82F6", bg: "bg-blue-50 border-blue-200" },
    bajo: { label: "Bajo", color: "#6B7280", bg: "bg-slate-50 border-slate-200" },
  };
  const p = config[priority] || config.normal;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${p.bg}`}
      style={{ color: p.color }}
      data-testid={`priority-badge-${priority || "normal"}`}
    >
      {p.label}
    </span>
  );
};

// Alert Indicator Component
const AlertIndicator = ({ ticket }) => {
  if (ticket.status === "resuelto") return <span className="text-slate-300 text-xs">—</span>;
  
  const alertLevel = ticket.alert_level;
  const minutes = ticket.minutes_without_resolution;
  
  if (!alertLevel) return <span className="text-slate-300 text-xs">—</span>;
  
  const config = {
    warning_15: { color: "text-yellow-500", bg: "bg-yellow-50 border-yellow-200", label: `${minutes}m`, icon: "fill-yellow-500" },
    warning_30: { color: "text-orange-500", bg: "bg-orange-50 border-orange-200", label: `${minutes}m`, icon: "fill-orange-500" },
    warning_60: { color: "text-red-500", bg: "bg-red-50 border-red-200", label: `${minutes}m`, icon: "fill-red-500" },
    critical: { color: "text-red-700", bg: "bg-red-100 border-red-300", label: `${minutes}m`, icon: "fill-red-700" },
  };
  
  const c = config[alertLevel] || config.warning_15;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded ${c.bg} ${c.color}`} title={`${minutes} min sin resolver`}>
      <Bell size={12} weight="fill" />
      {c.label}
    </span>
  );
};
const MONTHS = [
  { value: "1", label: "Enero" }, { value: "2", label: "Febrero" }, { value: "3", label: "Marzo" },
  { value: "4", label: "Abril" }, { value: "5", label: "Mayo" }, { value: "6", label: "Junio" },
  { value: "7", label: "Julio" }, { value: "8", label: "Agosto" }, { value: "9", label: "Septiembre" },
  { value: "10", label: "Octubre" }, { value: "11", label: "Noviembre" }, { value: "12", label: "Diciembre" }
];

const TicketsList = ({ onSelectTicket, onNewTicket }) => {
  const [tickets, setTickets] = useState([]);
  const [statusTypes, setStatusTypes] = useState([]);
  const [supportTypes, setSupportTypes] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [engineerFilter, setEngineerFilter] = useState("all");
  const [supportTypeFilter, setSupportTypeFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const { token, user } = useAuth();

  const fetchTickets = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (filterMonth !== "all") {
        params.append("month", filterMonth);
        params.append("year", filterYear);
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [ticketsRes, statusRes, stRes, usersRes] = await Promise.all([
        axios.get(`${API}/tickets${qs}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/status-types`),
        axios.get(`${API}/support-types`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setTickets(ticketsRes.data);
      setStatusTypes(statusRes.data);
      setSupportTypes(stRes.data || []);
      setEngineers((usersRes.data || []).filter(u => u.role === "ingeniero"));
    } catch (error) {
      toast.error("Error al cargar tickets");
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [token, dateFrom, dateTo, filterMonth, filterYear]);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/tickets/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tickets_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Archivo Excel descargado");
    } catch (error) {
      toast.error("Error al exportar tickets");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteTicket = async (ticketId, ticketNumber) => {
    if (!window.confirm(`¿Estás seguro de eliminar el ticket ${ticketNumber}? Esta acción no se puede deshacer.`)) return;
    setDeleting(ticketId);
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Ticket ${ticketNumber} eliminado`);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar ticket");
    } finally {
      setDeleting(null);
    }
  };

  const clearDateFilters = () => {
    setDateFrom("");
    setDateTo("");
    setFilterMonth("all");
    setFilterYear(String(new Date().getFullYear()));
  };

  const hasActiveDateFilters = dateFrom || dateTo || filterMonth !== "all";

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.contact_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.company || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || ticket.priority === priorityFilter;
    const matchesEngineer = engineerFilter === "all" || ticket.engineer_id === engineerFilter || (engineerFilter === "unassigned" && !ticket.engineer_id);
    const matchesSupportType = supportTypeFilter === "all" || ticket.support_type === supportTypeFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesEngineer && matchesSupportType;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / pageSize));
  const paginatedTickets = filteredTickets.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const startIdx = filteredTickets.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, filteredTickets.length);

  // When filters change, reset page
  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, priorityFilter, engineerFilter, supportTypeFilter]);

  const activeFilterCount = [statusFilter !== "all", priorityFilter !== "all", engineerFilter !== "all", supportTypeFilter !== "all", hasActiveDateFilters].filter(Boolean).length;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  return (
    <div className="form-section" data-testid="tickets-list">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-5">
        <h2 className="border-0 pb-0 mb-0">Tickets <span className="text-base font-normal text-slate-400 ml-2">({filteredTickets.length})</span></h2>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          {(user?.role === 'cliente' || user?.role === 'admin' || user?.role === 'ingeniero' || user?.role === 'auxiliar' || user?.role === 'comercial') && (
            <Button className="btn-primary" onClick={onNewTicket} data-testid="new-ticket-from-list">
              <Plus size={18} className="mr-2" /> Nuevo Ticket
            </Button>
          )}
          <Button variant="outline" className="btn-secondary" onClick={handleExportExcel} disabled={exporting} data-testid="export-excel-button">
            <FileXls size={18} className="mr-2" />
            {exporting ? "Exportando..." : "Excel"}
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5" data-testid="tickets-filter-bar">
        {/* Row 1: Search + quick filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
          <div className="relative lg:col-span-2">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input placeholder="Ticket / Contacto / Empresa / Descripcion..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="input-sharp pl-9 h-9 text-sm" data-testid="tickets-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="input-sharp h-9 text-sm" data-testid="status-filter">
              <SelectValue placeholder="Estatus" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estatus</SelectItem>
              {statusTypes.map(st => (
                <SelectItem key={st.id} value={st.name}>{st.label || st.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="input-sharp h-9 text-sm" data-testid="priority-filter">
              <SelectValue placeholder="Prioridad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las prioridades</SelectItem>
              <SelectItem value="critico">Critico</SelectItem>
              <SelectItem value="alto">Alto</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="bajo">Bajo</SelectItem>
            </SelectContent>
          </Select>
          <Select value={engineerFilter} onValueChange={setEngineerFilter}>
            <SelectTrigger className="input-sharp h-9 text-sm" data-testid="engineer-filter">
              <SelectValue placeholder="Ingeniero" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los ingenieros</SelectItem>
              <SelectItem value="unassigned">Sin asignar</SelectItem>
              {engineers.map(e => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Row 2: Support type + Date filters */}
        <div className="flex flex-wrap items-center gap-3">
          <Select value={supportTypeFilter} onValueChange={setSupportTypeFilter}>
            <SelectTrigger className="input-sharp h-9 text-sm w-44" data-testid="support-type-filter">
              <SelectValue placeholder="Tipo soporte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {supportTypes.map(st => (
                <SelectItem key={st.id} value={st.name}>{st.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className={`h-9 text-sm ${hasActiveDateFilters ? '!border-blue-500 !text-blue-600 !bg-blue-50' : ''}`}
            onClick={() => setShowDateFilters(!showDateFilters)}
            data-testid="toggle-date-filters"
          >
            <CalendarBlank size={14} className="mr-1.5" />
            Fechas
            {hasActiveDateFilters && <span className="ml-1 w-2 h-2 rounded-full bg-blue-500 inline-block" />}
          </Button>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-xs text-red-500 hover:text-red-700"
              onClick={() => { setStatusFilter("all"); setPriorityFilter("all"); setEngineerFilter("all"); setSupportTypeFilter("all"); clearDateFilters(); setSearchTerm(""); }}
              data-testid="clear-all-filters"
            >
              <X size={14} className="mr-1" /> Limpiar filtros ({activeFilterCount})
            </Button>
          )}
        </div>
        {showDateFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-200">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Desde</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-sharp h-9 text-sm" data-testid="date-from-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Hasta</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-sharp h-9 text-sm" data-testid="date-to-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Mes</Label>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="input-sharp h-9 text-sm" data-testid="month-filter"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {MONTHS.map(m => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Ano</Label>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="input-sharp h-9 text-sm" data-testid="year-filter"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map(y => (<SelectItem key={y} value={y}>{y}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Ticket Cards Table */}
      <div className="overflow-x-auto">
        <table className="ticket-table" data-testid="tickets-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Ticket</th>
              <th>Cliente / Empresa</th>
              <th>Soporte</th>
              <th>Responsable</th>
              <th>Estatus</th>
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTickets.map(ticket => {
              const primaryContact = ticket.contacts?.[0] || {};
              const contactCount = (ticket.contacts || []).length;
              const createdDate = format(new Date(ticket.created_at), "dd/MM/yyyy HH:mm");
              return (
                <tr key={ticket.id} className="group cursor-pointer hover:bg-blue-50/30" onClick={() => onSelectTicket(ticket.id)} data-testid={`ticket-row-${ticket.id}`}>
                  <td className="w-8" onClick={(e) => e.stopPropagation()}>
                    <AlertIndicator ticket={ticket} />
                  </td>
                  <td>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="ticket-id font-semibold text-blue-700">{ticket.ticket_number}</span>
                        {ticket.ticket_type && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${ticket.ticket_type === "campo" ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-blue-50 text-blue-600 border border-blue-200"}`} data-testid={`ticket-type-badge-${ticket.ticket_type}`}>
                            {ticket.ticket_type === "campo" ? "Campo" : "Oficina"}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <PriorityBadge priority={ticket.priority} />
                        <span className="text-[10px] text-slate-400 font-mono">{createdDate}</span>
                      </div>
                      {ticket.description && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-1 max-w-[280px]">{ticket.description}</p>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{primaryContact.name || ticket.contact_name || "Sin contacto"}</p>
                      <p className="text-xs text-slate-500">{ticket.company || <span className="text-slate-300 italic">Sin empresa</span>}</p>
                      {primaryContact.phone && (
                        <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                          <Phone size={10} /> {primaryContact.phone}
                          {contactCount > 1 && <span className="ml-1 text-blue-500">(+{contactCount - 1})</span>}
                        </p>
                      )}
                    </div>
                  </td>
                  <td>
                    {ticket.support_type ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 border border-slate-200 text-slate-600">
                        {ticket.support_type}
                      </span>
                    ) : <span className="text-slate-300">&mdash;</span>}
                  </td>
                  <td>
                    <div>
                      <p className="text-sm text-slate-700">{ticket.engineer_name || <span className="text-slate-400 text-xs italic">Sin asignar</span>}</p>
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={ticket.status} statusTypes={statusTypes} />
                  </td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => onSelectTicket(ticket.id)} className="hover:bg-blue-100 text-blue-600 h-8 px-2" data-testid={`view-ticket-${ticket.id}`}>
                        <CaretRight size={16} weight="bold" />
                      </Button>
                      {user?.role === 'admin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTicket(ticket.id, ticket.ticket_number)}
                          disabled={deleting === ticket.id}
                          className="hover:bg-red-50 text-red-400 hover:text-red-600 h-8 px-2"
                          data-testid={`delete-ticket-${ticket.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredTickets.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-12 text-slate-400">
                  <MagnifyingGlass size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No se encontraron tickets</p>
                  {activeFilterCount > 0 && <p className="text-xs mt-1">Intenta ajustar los filtros</p>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredTickets.length > 0 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-3" data-testid="tickets-pagination">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>Mostrar</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger className="input-sharp h-8 w-20 text-sm" data-testid="page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 15, 25, 50, 100].map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-slate-400">|</span>
            <span>{startIdx}–{endIdx} de {filteredTickets.length}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="h-8 px-3 text-xs" data-testid="prev-page">
              Anterior
            </Button>
            {(() => {
              const pages = [];
              const maxShow = 5;
              let start = Math.max(1, currentPage - Math.floor(maxShow / 2));
              let end = Math.min(totalPages, start + maxShow - 1);
              if (end - start + 1 < maxShow) start = Math.max(1, end - maxShow + 1);
              if (start > 1) { pages.push(1); if (start > 2) pages.push("..."); }
              for (let i = start; i <= end; i++) pages.push(i);
              if (end < totalPages) { if (end < totalPages - 1) pages.push("..."); pages.push(totalPages); }
              return pages.map((p, i) =>
                p === "..." ? (
                  <span key={`e${i}`} className="px-1 text-slate-400 text-xs">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`h-8 min-w-[32px] px-2 rounded text-xs font-medium transition-colors ${
                      currentPage === p ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid={`page-${p}`}
                  >
                    {p}
                  </button>
                )
              );
            })()}
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="h-8 px-3 text-xs" data-testid="next-page">
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
const NewTicketForm = ({ onSuccess }) => {
  const [ticketType, setTicketType] = useState(null); // null = selector, "oficina" | "campo"
  const [contacts, setContacts] = useState([{ name: "", phone: "", whatsapp: "" }]);
  const [company, setCompany] = useState("");
  const [description, setDescription] = useState("");
  const [customDate, setCustomDate] = useState(null);
  const [supportType, setSupportType] = useState("");
  const [supportTypes, setSupportTypes] = useState([]);
  const [priority, setPriority] = useState("normal");
  const [engineerId, setEngineerId] = useState("");
  const [engineers, setEngineers] = useState([]);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { token, user } = useAuth();
  const isEngineer = user?.role === "ingeniero" || user?.role === "subgerente";

  // Oficina-specific
  const [reportedBy, setReportedBy] = useState("");
  const [reportChannel, setReportChannel] = useState("");
  const [productName, setProductName] = useState("");
  const [actionPlan, setActionPlan] = useState("");
  const [slaTarget, setSlaTarget] = useState("");
  const [nextAction, setNextAction] = useState("");

  // Campo-specific
  const [location, setLocation] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");
  const [departureTime, setDepartureTime] = useState("");
  const [kmTolls, setKmTolls] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  const [partsUsed, setPartsUsed] = useState([{ qty: 1, code: "", description: "" }]);
  const [result, setResult] = useState("");
  const [techSignature, setTechSignature] = useState("");
  const [clientSignature, setClientSignature] = useState("");
  const [supervisorSignature, setSupervisorSignature] = useState("");
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [stRes, usersRes] = await Promise.all([
          axios.get(`${API}/support-types`, { headers }),
          axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
        ]);
        setSupportTypes(stRes.data || []);
        const allEngineers = (usersRes.data || []).filter(u => u.role === "ingeniero");
        setEngineers(allEngineers);
        if (user) setEngineerId(user.id);
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error("Error fetching data:", error);
      }
    };
    fetchData();
    if (user) {
      setContacts([{ name: "", phone: "", whatsapp: "" }]);
    }
  }, [user, token]);

  const addContact = () => { if (contacts.length < 5) setContacts([...contacts, { name: "", phone: "", whatsapp: "" }]); };
  const removeContact = (index) => { if (contacts.length > 1) setContacts(contacts.filter((_, i) => i !== index)); };
  const updateContact = (index, field, value) => { const nc = [...contacts]; nc[index][field] = value; setContacts(nc); };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith("image/"));
    if (evidenceFiles.length + files.length > 5) { toast.error("Maximo 5 imagenes"); return; }
    setEvidenceFiles(prev => [...prev, ...files]);
  };
  const removeEvidence = (idx) => setEvidenceFiles(prev => prev.filter((_, i) => i !== idx));

  // Parts helpers
  const addPart = () => setPartsUsed([...partsUsed, { qty: 1, code: "", description: "" }]);
  const removePart = (idx) => { if (partsUsed.length > 1) setPartsUsed(partsUsed.filter((_, i) => i !== idx)); };
  const updatePart = (idx, field, value) => { const np = [...partsUsed]; np[idx][field] = value; setPartsUsed(np); };

  // Calculate total time
  const totalTime = (() => {
    if (!arrivalTime || !departureTime) return "";
    const [ah, am] = arrivalTime.split(":").map(Number);
    const [dh, dm] = departureTime.split(":").map(Number);
    let diff = (dh * 60 + dm) - (ah * 60 + am);
    if (diff < 0) diff += 24 * 60;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}h ${m}min`;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const validContacts = contacts.filter(c => c.name || c.phone);
    if (validContacts.length === 0) { toast.error("Se requiere al menos un dato de contacto"); setIsLoading(false); return; }

    try {
      const data = {
        ticket_type: ticketType,
        contacts: validContacts,
        company: company.trim() || null,
        custom_date: customDate ? format(customDate, "yyyy-MM-dd") : null,
        priority,
        engineer_id: (engineerId && engineerId !== "none") ? engineerId : null,
        hardware_type: productName || null,
      };

      if (ticketType === "oficina") {
        data.description = description;
        data.support_type = supportType || null;
        data.report_channel = reportChannel || null;
      } else {
        data.description = workDescription || description;
        data.location = location || null;
        data.arrival_time = arrivalTime || null;
        data.departure_time = departureTime || null;
        data.total_time = totalTime || null;
        data.km_tolls = kmTolls || null;
        data.service_type = serviceType || null;
        data.work_description = workDescription || null;
        data.parts_used = partsUsed.filter(p => p.description.trim());
        data.result = result || null;
        data.tech_signature = techSignature || null;
        data.client_signature = clientSignature || null;
        data.supervisor_signature = supervisorSignature || null;
      }

      const res = await axios.post(`${API}/tickets`, data, { headers: { Authorization: `Bearer ${token}` } });
      const newTicketId = res.data?.id;
      if (newTicketId && evidenceFiles.length > 0) {
        for (const file of evidenceFiles) {
          const fd = new FormData();
          fd.append("file", file);
          await axios.post(`${API}/tickets/${newTicketId}/evidence`, fd, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          }).catch(() => {});
        }
      }
      toast.success("Ticket creado exitosamente");
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear ticket");
    } finally {
      setIsLoading(false);
    }
  };

  const priorityOptions = [
    { value: "critico", label: "Critico", color: "#EF4444", bg: "bg-red-50 border-red-200 text-red-700" },
    { value: "alto", label: "Alto", color: "#F97316", bg: "bg-orange-50 border-orange-200 text-orange-700" },
    { value: "normal", label: "Normal", color: "#3B82F6", bg: "bg-blue-50 border-blue-200 text-blue-700" },
    { value: "bajo", label: "Bajo", color: "#6B7280", bg: "bg-slate-50 border-slate-200 text-slate-600" },
  ];

  const channelOptions = ["WhatsApp", "Llamada", "Email", "Presencial"];
  const nextActionOptions = ["Visita en campo", "Soporte remoto", "Cotizar", "Escalar"];
  const serviceTypeOptions = ["Correctivo", "Preventivo", "Instalacion", "Capacitacion"];
  const resultOptions = [
    { value: "resuelto", label: "Resuelto", cls: "border-green-300 bg-green-50 text-green-700" },
    { value: "parcial", label: "Parcial", cls: "border-amber-300 bg-amber-50 text-amber-700" },
    { value: "segunda_visita", label: "2a Visita", cls: "border-blue-300 bg-blue-50 text-blue-700" },
    { value: "espera_refaccion", label: "Espera de Refaccion", cls: "border-slate-300 bg-slate-50 text-slate-600" },
  ];

  // ===== TYPE SELECTOR =====
  if (!ticketType) {
    return (
      <div className="form-section" data-testid="new-ticket-form">
        <h2>Nuevo Ticket de Soporte</h2>
        <p className="text-sm text-slate-500 mb-8">Selecciona el tipo de ticket que deseas crear</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl mx-auto">
          <button
            type="button"
            onClick={() => setTicketType("oficina")}
            className="group bg-white border-2 border-slate-200 rounded-2xl p-6 text-center hover:border-blue-400 hover:shadow-lg transition-all"
            data-testid="ticket-type-oficina"
          >
            <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-100 transition-colors">
              <Buildings size={28} weight="duotone" className="text-blue-600" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900 mb-1">Oficina</h3>
            <p className="text-xs text-slate-500 leading-relaxed">Resumen operativo. Se reporta desde oficina, se asigna ingeniero y se planifica la accion.</p>
          </button>
          <button
            type="button"
            onClick={() => setTicketType("campo")}
            className="group bg-white border-2 border-slate-200 rounded-2xl p-6 text-center hover:border-emerald-400 hover:shadow-lg transition-all"
            data-testid="ticket-type-campo"
          >
            <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-100 transition-colors">
              <Wrench size={28} weight="duotone" className="text-emerald-600" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900 mb-1">Campo</h3>
            <p className="text-xs text-slate-500 leading-relaxed">Orden de servicio. El ingeniero esta en sitio: registro de trabajo, refacciones, diagnostico y cierre.</p>
          </button>
        </div>
        <div className="flex justify-center mt-6">
          <Button type="button" variant="outline" className="btn-secondary" onClick={() => onSuccess?.()}>Cancelar</Button>
        </div>
      </div>
    );
  }

  const isOficina = ticketType === "oficina";
  const isCampo = ticketType === "campo";
  const themeColor = isOficina ? "blue" : "emerald";

  return (
    <div className="form-section" data-testid="new-ticket-form">
      {/* Header with type indicator */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setTicketType(null)} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-type-selector">
            <CaretRight size={16} className="rotate-180" />
          </button>
          <h2 className="border-0 pb-0 mb-0">
            Nuevo Ticket — <span className={`text-${themeColor}-600`}>{isOficina ? "Oficina" : "Campo"}</span>
          </h2>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${isOficina ? "border-blue-200 bg-blue-50 text-blue-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {isOficina ? <Buildings size={14} className="mr-1.5" /> : <Wrench size={14} className="mr-1.5" />}
          {isOficina ? "Oficina" : "Campo"}
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">

        {/* ===== SECTION 1: Client Info (shared) ===== */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
            <span className={`w-6 h-6 rounded-lg ${isOficina ? 'bg-blue-500' : 'bg-emerald-500'} text-white flex items-center justify-center text-[11px] font-extrabold`}>1</span>
            <span className="text-sm font-bold text-slate-900">{isCampo ? "Cliente y Ubicacion" : "Informacion Esencial"}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Empresa *</Label>
              <CompanyCombobox value={company} onChange={setCompany} placeholder="Buscar empresa o escribir..." className="mt-1" />
            </div>
            {isCampo && (
              <div>
                <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Ubicacion *</Label>
                <Input value={location} onChange={(e) => setLocation(e.target.value)} className="input-sharp mt-1" placeholder="Direccion de la visita" data-testid="campo-location" />
              </div>
            )}
            {isOficina && (
              <div>
                <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Fecha del incidente</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="input-sharp w-full mt-1 justify-start text-left font-normal" data-testid="ticket-date-picker">
                      <Clock size={16} className="mr-2 text-slate-400" />
                      {customDate ? format(customDate, "PPP", { locale: es }) : "Hoy (automatico)"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={customDate} onSelect={setCustomDate} locale={es} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>

          {/* Reportado por */}
          <div className="mt-4 border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Reportado por ({contacts.length}/5)</Label>
              {contacts.length < 5 && (
                <Button type="button" variant="outline" size="sm" onClick={addContact} className="h-7 text-xs" data-testid="add-contact-button">
                  <Plus size={12} className="mr-1" /> Agregar
                </Button>
              )}
            </div>
            {contacts.map((contact, index) => (
              <div key={`contact-${index}`} className="bg-slate-50 border border-slate-200 p-3 mb-2 rounded-lg" data-testid={`contact-${index}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase">Persona {index + 1}</span>
                  {contacts.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeContact(index)} className="text-red-500 h-6 w-6 p-0" data-testid={`remove-contact-${index}`}><X size={12} /></Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] text-slate-400 uppercase">Nombre *</Label>
                    <Input value={contact.name} onChange={(e) => updateContact(index, "name", e.target.value)} className="input-sharp mt-0.5 h-8 text-sm" placeholder="Nombre completo" data-testid={`contact-name-${index}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400 uppercase">Telefono *</Label>
                    <div className="relative">
                      <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                      <Input type="tel" value={contact.phone} onChange={(e) => updateContact(index, "phone", e.target.value)} className="input-sharp mt-0.5 h-8 text-sm pl-8" placeholder="55 1234 5678" data-testid={`contact-phone-${index}`} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400 uppercase">WhatsApp</Label>
                    <div className="relative">
                      <WhatsappLogo className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                      <Input type="tel" value={contact.whatsapp} onChange={(e) => updateContact(index, "whatsapp", e.target.value)} className="input-sharp mt-0.5 h-8 text-sm pl-8" placeholder="55 1234 5678" data-testid={`contact-whatsapp-${index}`} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== OFICINA SECTIONS ===== */}
        {isOficina && (
          <>
            {/* Section 2: Detail from client */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="w-6 h-6 rounded-lg bg-blue-500 text-white flex items-center justify-center text-[11px] font-extrabold">2</span>
                <span className="text-sm font-bold text-slate-900">Detalle Reportado por el Cliente</span>
              </div>
              <div>
                <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Descripcion del problema *</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="input-sharp mt-1 min-h-[100px]" placeholder="Describa el problema reportado por el cliente..." data-testid="ticket-description" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Canal</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="report-channel-selector">
                    {channelOptions.map(ch => (
                      <button key={ch} type="button" onClick={() => setReportChannel(reportChannel === ch ? "" : ch)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${reportChannel === ch ? "border-blue-400 bg-blue-50 text-blue-700 border-2" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                        data-testid={`channel-${ch.toLowerCase()}`}>{ch}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Producto / Equipo</Label>
                  <div className="mt-1">
                    <ProductSearchInput
                      token={token}
                      value={productName}
                      onSelect={(product) => setProductName(product.name)}
                      onManualChange={(v) => setProductName(v)}
                      placeholder="Buscar en catalogo o escribir..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Priority + Assignment */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="w-6 h-6 rounded-lg bg-blue-500 text-white flex items-center justify-center text-[11px] font-extrabold">3</span>
                <span className="text-sm font-bold text-slate-900">Asignacion y Prioridad</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Prioridad *</Label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="priority-selector">
                    {priorityOptions.map(p => (
                      <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${priority === p.value ? `${p.bg} border-2 shadow-sm` : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                        data-testid={`priority-${p.value}`}>{p.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Ingeniero Responsable</Label>
                  {isEngineer ? (
                    <div className="mt-1 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <User size={16} className="text-blue-500" />
                      <span className="text-sm font-medium text-blue-700">{user?.name} (Tu)</span>
                    </div>
                  ) : (
                    <Select value={engineerId} onValueChange={setEngineerId}>
                      <SelectTrigger className="input-sharp mt-1" data-testid="ticket-engineer-select">
                        <User size={16} className="mr-2 text-slate-400" /><SelectValue placeholder="Seleccionar responsable" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={user?.id || "none"}>{user?.name} (Tu)</SelectItem>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {engineers.filter(e => e.id !== user?.id).map(eng => (<SelectItem key={eng.id} value={eng.id}>{eng.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ===== CAMPO SECTIONS ===== */}
        {isCampo && (
          <>
            {/* Section 2: Field Work */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-[11px] font-extrabold">2</span>
                <span className="text-sm font-bold text-slate-900">Registro de Trabajo en Campo</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Hora de Llegada *</Label>
                  <Input type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} className="input-sharp mt-1" data-testid="campo-arrival" />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Hora de Salida</Label>
                  <Input type="time" value={departureTime} onChange={(e) => setDepartureTime(e.target.value)} className="input-sharp mt-1" data-testid="campo-departure" />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Tiempo Total</Label>
                  <Input value={totalTime} readOnly className="input-sharp mt-1 bg-slate-50 text-slate-500" data-testid="campo-total-time" />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">KM / Casetas</Label>
                  <Input value={kmTolls} onChange={(e) => setKmTolls(e.target.value)} className="input-sharp mt-1" placeholder="36 km · $0" data-testid="campo-km-tolls" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Tipo de Servicio</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="service-type-selector">
                    {serviceTypeOptions.map(st => (
                      <button key={st} type="button" onClick={() => setServiceType(serviceType === st ? "" : st)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${serviceType === st ? "border-emerald-400 bg-emerald-50 text-emerald-700 border-2" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                        data-testid={`service-type-${st.toLowerCase()}`}>{st}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Producto / Equipo</Label>
                  <div className="mt-1">
                    <ProductSearchInput
                      token={token}
                      value={productName}
                      onSelect={(product) => setProductName(product.name)}
                      onManualChange={(v) => setProductName(v)}
                      placeholder="Buscar en catalogo o escribir..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Diagnostic */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-[11px] font-extrabold">3</span>
                <span className="text-sm font-bold text-slate-900">Diagnostico y Acciones Realizadas</span>
              </div>
              <div>
                <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Descripcion del trabajo realizado *</Label>
                <Textarea value={workDescription} onChange={(e) => setWorkDescription(e.target.value)} className="input-sharp mt-1 min-h-[100px]" placeholder="Describa detalladamente el trabajo realizado en sitio..." data-testid="work-description" />
              </div>
            </div>

            {/* Section 4: Parts */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-[11px] font-extrabold">4</span>
                <span className="text-sm font-bold text-slate-900">Refacciones y Consumibles Utilizados</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="parts-table">
                  <thead>
                    <tr className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                      <th className="text-left py-2 px-1 w-10">#</th>
                      <th className="text-left py-2 px-1 w-16">Cant.</th>
                      <th className="text-left py-2 px-1 w-32">Codigo</th>
                      <th className="text-left py-2 px-1">Descripcion</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {partsUsed.map((part, idx) => (
                      <tr key={`part-${idx}`} className="border-t border-slate-100">
                        <td className="py-1.5 px-1 text-xs text-slate-400">{idx + 1}</td>
                        <td className="py-1.5 px-1">
                          <Input type="number" min="1" value={part.qty} onChange={(e) => updatePart(idx, "qty", parseInt(e.target.value) || 1)} className="input-sharp h-8 text-sm w-14 text-center" data-testid={`part-qty-${idx}`} />
                        </td>
                        <td className="py-1.5 px-1">
                          <Input value={part.code} onChange={(e) => updatePart(idx, "code", e.target.value)} className="input-sharp h-8 text-sm" placeholder="KIT-LIMP" data-testid={`part-code-${idx}`} />
                        </td>
                        <td className="py-1.5 px-1">
                          <Input value={part.description} onChange={(e) => updatePart(idx, "description", e.target.value)} className="input-sharp h-8 text-sm" placeholder="Descripcion de la pieza" data-testid={`part-desc-${idx}`} />
                        </td>
                        <td className="py-1.5 px-1">
                          {partsUsed.length > 1 && (
                            <button type="button" onClick={() => removePart(idx)} className="text-red-400 hover:text-red-600" data-testid={`remove-part-${idx}`}><X size={14} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={addPart} className="text-emerald-600 text-xs font-semibold mt-2 flex items-center gap-1 hover:text-emerald-700" data-testid="add-part-btn">
                <Plus size={14} /> Agregar refaccion
              </button>
            </div>

            {/* Section 5: Closure */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-[11px] font-extrabold">5</span>
                <span className="text-sm font-bold text-slate-900">Cierre y Conformidad</span>
              </div>
              <div>
                <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Resultado *</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="result-selector">
                  {resultOptions.map(r => (
                    <button key={r.value} type="button" onClick={() => setResult(result === r.value ? "" : r.value)}
                      className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-all ${result === r.value ? `${r.cls} border-2 shadow-sm` : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                      data-testid={`result-${r.value}`}>{r.label}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Firma del Tecnico</Label>
                  <Input value={techSignature} onChange={(e) => setTechSignature(e.target.value)} className="input-sharp mt-1" placeholder="Nombre del tecnico" data-testid="tech-signature" />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Firma del Cliente</Label>
                  <Input value={clientSignature} onChange={(e) => setClientSignature(e.target.value)} className="input-sharp mt-1" placeholder="Nombre del cliente" data-testid="client-signature" />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Vo.Bo. Supervisor</Label>
                  <Input value={supervisorSignature} onChange={(e) => setSupervisorSignature(e.target.value)} className="input-sharp mt-1" placeholder="Nombre del supervisor" data-testid="supervisor-signature" />
                </div>
              </div>
            </div>

            {/* Priority + Engineer for Campo too */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-[11px] font-extrabold">6</span>
                <span className="text-sm font-bold text-slate-900">Asignacion y Prioridad</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Prioridad</Label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="priority-selector">
                    {priorityOptions.map(p => (
                      <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${priority === p.value ? `${p.bg} border-2 shadow-sm` : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                        data-testid={`priority-${p.value}`}>{p.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide">Ingeniero Responsable</Label>
                  {isEngineer ? (
                    <div className="mt-1 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                      <User size={16} className="text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700">{user?.name} (Tu)</span>
                    </div>
                  ) : (
                    <Select value={engineerId} onValueChange={setEngineerId}>
                      <SelectTrigger className="input-sharp mt-1" data-testid="ticket-engineer-select">
                        <User size={16} className="mr-2 text-slate-400" /><SelectValue placeholder="Seleccionar responsable" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={user?.id || "none"}>{user?.name} (Tu)</SelectItem>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {engineers.filter(e => e.id !== user?.id).map(eng => (<SelectItem key={eng.id} value={eng.id}>{eng.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ===== EVIDENCE (shared) ===== */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
          <Label className="text-slate-600 text-xs font-bold uppercase tracking-wide mb-3 block">Evidencia Fotografica ({evidenceFiles.length}/5)</Label>
          <div className="flex flex-wrap gap-3 mb-3">
            {evidenceFiles.map((file, idx) => (
              <div key={file.name || `ev-${idx}`} className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-slate-200 bg-white group cursor-pointer" onClick={() => setPreviewImage(URL.createObjectURL(file))}>
                <img src={URL.createObjectURL(file)} alt={`Evidencia ${idx + 1}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <MagnifyingGlass size={18} weight="bold" className="text-white opacity-0 group-hover:opacity-100 drop-shadow-lg" />
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeEvidence(idx); }} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-evidence-${idx}`}><X size={12} /></button>
              </div>
            ))}
            {evidenceFiles.length < 5 && (
              <>
                <label className="w-24 h-24 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/50 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-100/50 active:scale-95 transition-all" data-testid="upload-evidence-btn">
                  <Camera size={24} className="text-blue-500 mb-1" /><span className="text-[10px] text-blue-500 font-bold">Galeria</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} data-testid="evidence-file-input" />
                </label>
                <label className="w-24 h-24 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 flex flex-col items-center justify-center cursor-pointer hover:bg-emerald-100/50 active:scale-95 transition-all" data-testid="capture-evidence-btn">
                  <Camera size={24} className="text-emerald-600 mb-1" /><span className="text-[10px] text-emerald-600 font-bold">Camara</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} data-testid="evidence-camera-input" />
                </label>
              </>
            )}
          </div>
        </div>
        {previewImage && (
          <ImageLightbox images={[previewImage]} startIndex={0} onClose={() => setPreviewImage(null)} />
        )}

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" className="btn-secondary" onClick={() => onSuccess?.()}>Cancelar</Button>
          <Button type="button" variant="outline" data-testid="preview-pdf-btn"
            className="!border-slate-300 !text-slate-700 hover:!bg-slate-50"
            onClick={async () => {
              try {
                const previewData = {
                  ticket_type: ticketType,
                  contacts: contacts.filter(c => c.name || c.phone),
                  company: company.trim() || null,
                  priority,
                  hardware_type: productName || null,
                  ...(isOficina ? {
                    description,
                    report_channel: reportChannel || null,
                  } : {
                    location: location || null,
                    arrival_time: arrivalTime || null,
                    departure_time: departureTime || null,
                    total_time: totalTime || null,
                    km_tolls: kmTolls || null,
                    service_type: serviceType || null,
                    work_description: workDescription || null,
                    description: workDescription || null,
                    parts_used: partsUsed.filter(p => p.description.trim()),
                    result: result || null,
                    tech_signature: techSignature || null,
                    client_signature: clientSignature || null,
                    supervisor_signature: supervisorSignature || null,
                  }),
                };
                const res = await axios.post(`${API}/tickets/preview-pdf`, previewData, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                window.open(url, "_blank");
              } catch { toast.error("Error al generar vista previa"); }
            }}>
            <FilePdf size={16} className="mr-1.5" /> Vista Previa PDF
          </Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-ticket"
            className={isOficina ? "btn-primary" : "bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-semibold"}>
            {isLoading ? "Creando..." : `Crear Ticket ${isOficina ? "Oficina" : "Campo"}`}
          </Button>
        </div>
      </form>
    </div>
  );
};
const TicketDetail = ({ ticketId, onBack }) => {
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [statusTypes, setStatusTypes] = useState([]);
  const [softwareTypes, setSoftwareTypes] = useState([]);
  const [hardwareTypes, setHardwareTypes] = useState([]);
  const [policyTypes, setPolicyTypes] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [engineers, setEngineers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [engineerCommentsLocal, setEngineerCommentsLocal] = useState("");
  const [savingComments, setSavingComments] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionLocal, setDescriptionLocal] = useState("");
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyLocal, setCompanyLocal] = useState("");
  const [supportTypes, setSupportTypes] = useState([]);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [pdfTemplate, setPdfTemplate] = useState("profesional");
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [centerTab, setCenterTab] = useState("historial");
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { token, user } = useAuth();

  const fetchTicket = async () => {
    try {
      const [ticketRes, commentsRes, engineersRes, historyRes, statusRes, swRes, hwRes, polRes, stRes] = await Promise.all([
        axios.get(`${API}/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tickets/${ticketId}/comments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/engineers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tickets/${ticketId}/history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/status-types`),
        axios.get(`${API}/software-types`),
        axios.get(`${API}/hardware-types`),
        axios.get(`${API}/policy-types`),
        axios.get(`${API}/support-types`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTicket(ticketRes.data);
      setComments(commentsRes.data);
      setEngineers(engineersRes.data);
      setHistory(historyRes.data);
      setStatusTypes(statusRes.data);
      setSoftwareTypes(swRes.data);
      setHardwareTypes(hwRes.data);
      setPolicyTypes(polRes.data);
      setSupportTypes(stRes.data || []);
      setEngineerCommentsLocal(ticketRes.data.engineer_comments || "");
      setDescriptionLocal(ticketRes.data.description || "");
      setCompanyLocal(ticketRes.data.company || "");
      // Load ticket config for PDF template
      try {
        const cfgRes = await axios.get(`${API}/ticket-config`, { headers: { Authorization: `Bearer ${token}` } });
        if (cfgRes.data?.selected_template) setPdfTemplate(cfgRes.data.selected_template);
      } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    } catch (error) {
      toast.error("Error al cargar ticket");
      onBack();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [ticketId, token]);

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Estatus actualizado");
      fetchTicket();
    } catch (error) {
      toast.error("Error al actualizar estatus");
    }
  };

  const handleEngineerAssign = async (engineerId) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { engineer_id: engineerId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ingeniero asignado");
      fetchTicket();
    } catch (error) {
      toast.error("Error al asignar ingeniero");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/tickets/${ticketId}/comments`, { content: newComment }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewComment("");
      toast.success("Comentario agregado");
      fetchTicket();
    } catch (error) {
      toast.error("Error al agregar comentario");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (ticket.evidence_images?.length >= 5) {
      toast.error("Máximo 5 imágenes permitidas");
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/tickets/${ticketId}/evidence`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Imagen subida exitosamente");
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleUpdateField = async (field, value) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { [field]: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ticket actualizado");
      fetchTicket();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleSaveComments = async () => {
    setSavingComments(true);
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { engineer_comments: engineerCommentsLocal }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Comentarios guardados");
      fetchTicket();
    } catch (error) {
      toast.error("Error al guardar comentarios");
    } finally {
      setSavingComments(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}/export-pdf?template=${pdfTemplate}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket_${ticket.ticket_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al exportar PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleWhatsappPdf = async () => {
    setSendingWhatsapp(true);
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket_${ticket.ticket_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const contactWhatsapp = ticket.contacts?.[0]?.whatsapp || ticket.contacts?.[0]?.phone || "";
      const cleanNumber = contactWhatsapp.replace(/\D/g, "");
      const message = encodeURIComponent(`Hola, le comparto el reporte PDF del ticket ${ticket.ticket_number} de MaxTicket. Por favor revise el archivo descargado.`);
      const waUrl = cleanNumber ? `https://wa.me/${cleanNumber}?text=${message}` : `https://wa.me/?text=${message}`;
      window.open(waUrl, "_blank");
      toast.success("PDF descargado. WhatsApp abierto.");
    } catch (error) {
      toast.error("Error al exportar PDF");
    } finally {
      setSendingWhatsapp(false);
    }
  };

  const handleSaveDescription = async () => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { description: descriptionLocal }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Descripción actualizada");
      setEditingDescription(false);
      fetchTicket();
    } catch (error) {
      toast.error("Error al actualizar descripción");
    }
  };

  const handleSaveCompany = async () => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { company: companyLocal }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Empresa actualizada");
      setEditingCompany(false);
      fetchTicket();
    } catch (error) {
      toast.error("Error al actualizar empresa");
    }
  };

  if (isLoading) {
    return <div className="form-section">Cargando ticket...</div>;
  }

  if (!ticket) {
    return <div className="form-section">Ticket no encontrado</div>;
  }

  const canEdit = user?.role === "admin" || user?.role === "ingeniero" || user?.role === "auxiliar" || user?.role === "comercial";

  // Tab state for center panel - hook moved to top

  // Build unified timeline from history + comments
  const timelineItems = [
    ...history.map(h => ({ ...h, _type: "history", _date: h.created_at })),
    ...comments.map(c => ({ ...c, _type: "comment", _date: c.created_at })),
  ].sort((a, b) => new Date(b._date) - new Date(a._date));

  const filteredTimeline = centerTab === "notas"
    ? timelineItems.filter(i => i._type === "comment")
    : centerTab === "evidencias"
    ? timelineItems.filter(i => i.action === "evidence_added")
    : timelineItems;

  const getTimelineIcon = (item) => {
    if (item._type === "comment") return { icon: <ChatText size={13} />, cls: "bg-violet-100 text-violet-600" };
    const a = item.action;
    if (a === "created") return { icon: <Plus size={13} />, cls: "bg-blue-100 text-blue-600" };
    if (a === "status_changed" || a === "reopened") return { icon: <ArrowsClockwise size={13} />, cls: "bg-amber-100 text-amber-600" };
    if (a === "engineer_assigned") return { icon: <User size={13} />, cls: "bg-slate-100 text-slate-500" };
    if (a === "evidence_added") return { icon: <Camera size={13} />, cls: "bg-pink-100 text-pink-600" };
    if (a === "link_shared") return { icon: <Share size={13} />, cls: "bg-teal-100 text-teal-600" };
    return { icon: <PencilSimple size={13} />, cls: "bg-slate-100 text-slate-500" };
  };

  return (
    <div className="h-full flex flex-col" data-testid="ticket-detail">
      {/* Top Bar */}
      <div className="bg-white border-b px-4 py-2 flex items-center gap-3 shrink-0 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-blue-600 text-xs" data-testid="back-button">
          <CaretRight size={14} className="rotate-180 mr-1" /> Tickets
        </Button>
        <span className="text-slate-300">|</span>
        <span className="text-sm font-bold">{ticket.ticket_number}</span>
        {ticket.company && <span className="text-xs text-slate-400">· {ticket.company}</span>}
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="text-xs !border-teal-300 !text-teal-700 hover:!bg-teal-50" data-testid="share-ticket-link"
            onClick={async () => {
              try {
                const { data } = await axios.post(`${API}/tickets/${ticket.id}/share`, { hours: 24 }, { headers: { Authorization: `Bearer ${token}` } });
                const url = `${window.location.origin}/ticket/${data.id}`;
                const text = `Seguimiento de Ticket ${ticket.ticket_number}\n${ticket.description?.slice(0,80)}\n\nVer estatus: ${url}`;
                navigator.clipboard.writeText(url).then(() => toast.success("Link copiado")).catch(() => {});
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
              } catch { toast.error("Error al crear link"); }
            }}>
            <Share size={14} className="mr-1" /> Compartir
          </Button>
          <Button variant="outline" size="sm" className="text-xs !border-green-300 !text-green-700" onClick={handleWhatsappPdf} disabled={sendingWhatsapp}>
            <WhatsappLogo size={14} weight="fill" className="mr-1" /> PDF WhatsApp
          </Button>
          <Button variant="outline" size="sm" className="text-xs" onClick={handleExportPdf} disabled={exportingPdf} data-testid="export-ticket-pdf">
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[280px_1fr_260px] min-h-0 overflow-hidden">

        {/* ===== LEFT SIDEBAR ===== */}
        <div className="bg-white border-r overflow-y-auto p-4 space-y-3">
          {/* Ticket badge */}
          <div className={`bg-gradient-to-r ${ticket.ticket_type === "campo" ? "from-emerald-700 to-emerald-500" : "from-blue-700 to-blue-500"} text-white rounded-xl px-4 py-3 text-center`}>
            <p className="text-[9px] font-semibold opacity-70 uppercase tracking-wider">Ticket {ticket.ticket_type === "campo" ? "Campo" : "Oficina"}</p>
            <p className="text-lg font-extrabold">{ticket.ticket_number}</p>
          </div>

          {/* Priority + Status */}
          <div className="flex justify-center gap-2">
            <PriorityBadge priority={ticket.priority} />
            <StatusBadge status={ticket.status} statusTypes={statusTypes} />
          </div>

          {/* Company */}
          <div className="text-center pb-3 border-b">
            <p className="text-base font-extrabold text-slate-900">{ticket.company || "Sin empresa"}</p>
            <p className="text-[10px] text-slate-400">{ticket.support_type || ""}</p>
          </div>

          {/* Quick Actions */}
          {canEdit && (
            <div className="flex justify-center gap-1 pb-3 border-b">
              {[
                { icon: <Camera size={14} />, label: "Foto", action: () => document.getElementById("upload-evidence-input")?.click() },
                { icon: <ChatText size={14} />, label: "Nota", action: () => document.getElementById("note-input-main")?.focus() },
              ].map((a, i) => (
                <button key={i} onClick={a.action} className="w-11 h-11 rounded-lg border border-slate-200 flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors text-[8px] font-semibold">
                  {a.icon}{a.label}
                </button>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-11 h-11 rounded-lg border border-slate-200 flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors text-[8px] font-semibold">
                    <ArrowsClockwise size={14} />Estatus
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {statusTypes.map(st => <DropdownMenuItem key={st.id} onClick={() => handleStatusChange(st.name)} data-testid={`status-${st.name}`}><span className="w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: st.color }} />{st.label}</DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-11 h-11 rounded-lg border border-slate-200 flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors text-[8px] font-semibold">
                    <User size={14} />Asignar
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleEngineerAssign("")}>Sin asignar</DropdownMenuItem>
                  {engineers.map(eng => <DropdownMenuItem key={eng.id} onClick={() => handleEngineerAssign(eng.id)}>{eng.name}</DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Product / Equipment - replaces hardware/software */}
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Producto / Equipo</p>
            {ticket.hardware_type && (
              <div className="bg-slate-50 border rounded-lg p-2.5 mb-1.5">
                <p className="text-xs font-bold text-slate-800">{ticket.hardware_type}</p>
                {ticket.software_type && <p className="text-[10px] text-slate-500">{ticket.software_type}</p>}
              </div>
            )}
            {canEdit && (
              <ProductSearchForTicket token={token} currentValue={ticket.hardware_type || ""} onSelect={(name) => handleUpdateField("hardware_type", name)} />
            )}
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Descripcion</p>
              {canEdit && !editingDescription && <button onClick={() => { setEditingDescription(true); setDescriptionLocal(ticket.description || ""); }} className="text-[10px] text-blue-600"><PencilSimple size={10} /></button>}
            </div>
            {editingDescription ? (
              <div>
                <Textarea value={descriptionLocal} onChange={e => setDescriptionLocal(e.target.value)} className="text-xs min-h-[60px]" data-testid="edit-description-input" />
                <div className="flex gap-1 mt-1"><Button size="sm" className="h-6 text-[10px]" onClick={handleSaveDescription}>Guardar</Button><Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setEditingDescription(false)}>X</Button></div>
              </div>
            ) : (
              <p className="text-xs text-slate-600 bg-slate-50 border rounded-lg p-2.5 leading-relaxed">{ticket.description || "Sin descripcion"}</p>
            )}
          </div>

          {/* Contact */}
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Contacto</p>
            {(ticket.contacts?.length > 0 ? ticket.contacts : [{ name: ticket.contact_name, phone: ticket.phone, whatsapp: ticket.whatsapp }]).map((ct, i) => (
              <div key={`ct-${i}`} className="bg-slate-50 border rounded-lg p-2.5 mb-1.5">
                <p className="text-xs font-bold text-slate-800">{ct.name}</p>
                {ct.phone && <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5"><Phone size={10} /> {ct.phone}</p>}
                {ct.whatsapp && <p className="text-[10px] text-green-600 flex items-center gap-1"><WhatsappLogo size={10} weight="fill" /> {ct.whatsapp}</p>}
              </div>
            ))}
          </div>

          {/* Fields */}
          <div className="space-y-2 border-t pt-3">
            <Field label="Ingeniero" value={ticket.engineer_name || "Sin asignar"} blue={!!ticket.engineer_name} />
            <Field label="Tipo Soporte" value={ticket.support_type || "—"} />
            {canEdit && (
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Prioridad</p>
                <div className="flex gap-1 mt-1">
                  {[{ v: "alto", l: "Alto", c: "text-orange-600 border-orange-200 bg-orange-50" }, { v: "normal", l: "Normal", c: "text-blue-600 border-blue-200 bg-blue-50" }, { v: "bajo", l: "Bajo", c: "text-slate-500 border-slate-200 bg-slate-50" }].map(p => (
                    <button key={p.v} onClick={() => handleUpdateField("priority", p.v)} className={`px-2 py-0.5 rounded border text-[10px] font-semibold ${ticket.priority === p.v ? p.c + " border-2" : "border-slate-200 text-slate-400"}`} data-testid={`edit-priority-${p.v}`}>{p.l}</button>
                  ))}
                </div>
              </div>
            )}
            <Field label="Creado" value={format(new Date(ticket.created_at), "dd MMM yyyy, HH:mm", { locale: es })} />
            {ticket.resolution_time && <Field label="Tiempo Resolucion" value={ticket.resolution_time} amber />}
            {ticket.policy_type_name && <Field label="Poliza" value={ticket.policy_type_name} green />}

            {/* Oficina fields */}
            {ticket.ticket_type === "oficina" && (
              <>
                {ticket.reported_by && <Field label="Reportado por" value={ticket.reported_by} />}
                {ticket.report_channel && <Field label="Canal" value={ticket.report_channel} />}
                {ticket.sla_target && <Field label="SLA Objetivo" value={ticket.sla_target} amber />}
                {ticket.next_action && <Field label="Siguiente Accion" value={ticket.next_action} blue />}
              </>
            )}

            {/* Campo fields */}
            {ticket.ticket_type === "campo" && (
              <>
                {ticket.location && <Field label="Ubicacion" value={ticket.location} />}
                {ticket.service_type && <Field label="Tipo Servicio" value={ticket.service_type} />}
                {ticket.arrival_time && <Field label="Llegada" value={ticket.arrival_time} />}
                {ticket.departure_time && <Field label="Salida" value={ticket.departure_time} />}
                {ticket.total_time && <Field label="Tiempo Total" value={ticket.total_time} amber />}
                {ticket.km_tolls && <Field label="KM / Casetas" value={ticket.km_tolls} />}
                {ticket.result && (
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Resultado</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold mt-0.5 ${
                      ticket.result === "resuelto" ? "bg-green-50 text-green-700 border border-green-200" :
                      ticket.result === "parcial" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                      "bg-slate-50 text-slate-600 border border-slate-200"
                    }`}>{ticket.result === "segunda_visita" ? "2a Visita" : ticket.result === "espera_refaccion" ? "Espera Refaccion" : ticket.result?.charAt(0).toUpperCase() + ticket.result?.slice(1)}</span>
                  </div>
                )}
                {ticket.tech_signature && <Field label="Firma Tecnico" value={ticket.tech_signature} />}
                {ticket.client_signature && <Field label="Firma Cliente" value={ticket.client_signature} />}
                {ticket.supervisor_signature && <Field label="Vo.Bo. Supervisor" value={ticket.supervisor_signature} />}
              </>
            )}
          </div>
        </div>

        {/* ===== CENTER: TIMELINE ===== */}
        <div className="bg-white flex flex-col min-h-0 overflow-hidden">
          <div className="flex border-b px-4 shrink-0">
            {[{id: "historial", l: "Historial"}, {id: "notas", l: "Notas"}, {id: "evidencias", l: "Evidencias"}].map(t => (
              <button key={t.id} onClick={() => setCenterTab(t.id)} className={`px-3 py-2.5 text-xs font-semibold border-b-2 transition-colors ${centerTab === t.id ? "text-blue-600 border-blue-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t.l}</button>
            ))}
          </div>

          {/* Timeline */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {filteredTimeline.map((item, idx) => {
              const { icon, cls } = getTimelineIcon(item);
              return (
                <div key={`${item._type}-${item.id}-${idx}`} className="flex gap-2.5 relative">
                  {idx < filteredTimeline.length - 1 && <div className="absolute left-[13px] top-7 bottom-0 w-px bg-slate-100" />}
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 z-10 ${cls}`}>{icon}</div>
                  <div className="flex-1 min-w-0 pb-2">
                    <p className="text-xs font-semibold text-slate-800">
                      {item._type === "comment" ? <>Comentario — <span className="text-blue-600">{item.user_name}</span></> : item.details}
                    </p>
                    <p className="text-[10px] text-slate-400">{format(new Date(item._date), "dd MMM yyyy, HH:mm", { locale: es })} {item.user_name && item._type === "history" ? `· ${item.user_name}` : ""}</p>
                    {item._type === "comment" && item.content && <div className="mt-1.5 text-xs text-slate-600 bg-slate-50 border rounded-lg p-2.5 leading-relaxed">{item.content}</div>}
                    {item.old_value && item.new_value && <p className="text-[10px] mt-1"><span className="text-slate-400 line-through">{item.old_value}</span> → <span className="text-slate-700 font-semibold">{item.new_value}</span></p>}
                  </div>
                </div>
              );
            })}
            {filteredTimeline.length === 0 && <p className="text-center text-xs text-slate-400 py-10">Sin actividad</p>}
          </div>

          {/* Evidence inline */}
          {ticket.evidence_images?.length > 0 && (
            <div className="border-t px-4 py-3 shrink-0">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Evidencias ({ticket.evidence_images.length})</p>
                <div className="flex gap-2">
                  <label className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 text-xs font-bold flex items-center gap-1.5 cursor-pointer hover:bg-blue-100 active:scale-95 transition-all min-h-[40px]" data-testid="detail-upload-gallery">
                    <Camera size={16} /> Galeria
                    <input type="file" className="hidden" accept="image/*" multiple onChange={handleFileUpload} disabled={uploading} />
                  </label>
                  <label className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600 text-xs font-bold flex items-center gap-1.5 cursor-pointer hover:bg-emerald-100 active:scale-95 transition-all min-h-[40px]" data-testid="detail-upload-camera">
                    <Camera size={16} /> Camara
                    <input type="file" className="hidden" accept="image/*" capture="environment" onChange={handleFileUpload} disabled={uploading} />
                  </label>
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {ticket.evidence_images.map((fileId, idx) => (
                  <div key={fileId} className="w-20 h-16 rounded-lg overflow-hidden shrink-0 border-2 border-slate-200 cursor-pointer" data-testid={`evidence-thumb-${idx}`}>
                    <EvidenceImage fileId={fileId} token={token} onClick={(src) => {
                      setLightboxImages(prev => { const n = [...prev]; n[idx] = src; return n; });
                      setLightboxIndex(idx);
                    }} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {!ticket.evidence_images?.length && (
            <div className="border-t px-4 py-3 shrink-0">
              <div className="flex gap-3">
                <label className="flex-1 flex items-center justify-center gap-2 py-4 border-2 border-dashed border-blue-300 bg-blue-50/50 rounded-xl cursor-pointer text-sm text-blue-600 font-bold hover:bg-blue-100/50 active:scale-95 transition-all min-h-[52px]" data-testid="detail-upload-gallery-empty">
                  <input type="file" className="hidden" accept="image/*" multiple onChange={handleFileUpload} disabled={uploading} />
                  <Camera size={20} /> {uploading ? "Subiendo..." : "Galeria"}
                </label>
                <label className="flex-1 flex items-center justify-center gap-2 py-4 border-2 border-dashed border-emerald-300 bg-emerald-50/50 rounded-xl cursor-pointer text-sm text-emerald-600 font-bold hover:bg-emerald-100/50 active:scale-95 transition-all min-h-[52px]" data-testid="detail-upload-camera-empty">
                  <input type="file" className="hidden" accept="image/*" capture="environment" onChange={handleFileUpload} disabled={uploading} />
                  <Camera size={20} /> {uploading ? "Subiendo..." : "Camara"}
                </label>
              </div>
            </div>
          )}

          {/* Comment input */}
          <div className="border-t bg-slate-50 p-4 shrink-0">
            <div className="flex gap-2">
              <Textarea id="note-input-main" value={newComment} onChange={e => setNewComment(e.target.value)} className="text-sm min-h-[50px] flex-1" placeholder="Escribe un comentario o nota tecnica..." data-testid="new-comment-input" />
              <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()} className="self-end min-h-[40px] px-4" data-testid="submit-comment"><ChatText size={16} className="mr-1" /> Enviar</Button>
            </div>
            {canEdit && (
              <div className="mt-4 bg-white border border-blue-200 rounded-xl p-4">
                <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5"><NotePencil size={14} /> Notas de Ingenieria</p>
                <Textarea value={engineerCommentsLocal} onChange={e => setEngineerCommentsLocal(e.target.value)} className="text-sm min-h-[100px] mb-2" placeholder="Escribe aqui las notas tecnicas, diagnostico, solucion aplicada..." data-testid="engineer-comments" />
                <Button size="sm" onClick={handleSaveComments} disabled={savingComments || engineerCommentsLocal === (ticket.engineer_comments || "")} className="btn-primary h-9 px-5 text-xs" data-testid="save-engineer-comments">
                  {savingComments ? "Guardando..." : "Guardar Notas"}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* ===== RIGHT SIDEBAR ===== */}
        <div className="bg-white border-l overflow-y-auto">
          {/* Evidence */}
          <RSSection title="Evidencias" count={ticket.evidence_images?.length || 0} icon={<Camera size={13} />}>
            {(ticket.evidence_images || []).map((fileId, idx) => (
              <div key={fileId} className="w-full h-24 rounded-lg overflow-hidden border-2 border-slate-200 mb-1.5 cursor-pointer" data-testid={`rs-evidence-${idx}`}>
                <EvidenceImage fileId={fileId} token={token} onClick={(src) => {
                  setLightboxImages(prev => { const n = [...prev]; n[idx] = src; return n; });
                  setLightboxIndex(idx);
                }} />
              </div>
            ))}
            {canEdit && (ticket.evidence_images?.length || 0) < 5 && (
              <div className="flex gap-2 mt-2">
                <label className="flex-1 flex items-center justify-center gap-1.5 py-3 border-2 border-dashed border-blue-200 rounded-xl cursor-pointer text-xs text-blue-500 font-semibold hover:bg-blue-50 active:scale-95 transition-all min-h-[44px]" data-testid="rs-upload-gallery">
                  <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                  <Camera size={14} /> Galeria
                </label>
                <label className="flex-1 flex items-center justify-center gap-1.5 py-3 border-2 border-dashed border-emerald-200 rounded-xl cursor-pointer text-xs text-emerald-600 font-semibold hover:bg-emerald-50 active:scale-95 transition-all min-h-[44px]" data-testid="rs-upload-camera">
                  <input type="file" className="hidden" accept="image/*" capture="environment" onChange={handleFileUpload} disabled={uploading} />
                  <Camera size={14} /> Camara
                </label>
              </div>
            )}
          </RSSection>

          {/* Product */}
          <RSSection title="Producto" count={ticket.hardware_type ? 1 : 0} icon={<Cpu size={13} />}>
            {ticket.hardware_type && (
              <div className="p-2 bg-slate-50 border rounded-lg text-xs mb-1.5">
                <p className="font-bold text-slate-800">{ticket.hardware_type}</p>
                {ticket.software_type && <p className="text-[10px] text-slate-400">{ticket.software_type}</p>}
              </div>
            )}
          </RSSection>

          {/* Policy */}
          <RSSection title="Poliza" count={ticket.client_has_policy ? 1 : 0} icon={<Shield size={13} />}>
            {ticket.client_has_policy ? (
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs flex items-center justify-between">
                <div>
                  <p className="font-bold text-emerald-800">{ticket.policy_type_name || "Con poliza"}</p>
                </div>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Activa</span>
              </div>
            ) : <p className="text-[10px] text-slate-400 text-center py-2">Sin poliza</p>}
          </RSSection>

          {/* Oficina: Plan de acción */}
          {ticket.ticket_type === "oficina" && ticket.action_plan && (
            <RSSection title="Plan de Accion" count={1} icon={<ClockCounterClockwise size={13} />}>
              <p className="text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded-lg p-2.5 leading-relaxed">{ticket.action_plan}</p>
            </RSSection>
          )}

          {/* Campo: Work + Parts + Signatures */}
          {ticket.ticket_type === "campo" && (
            <>
              {ticket.work_description && (
                <RSSection title="Trabajo Realizado" count={1} icon={<Wrench size={13} />}>
                  <p className="text-xs text-slate-600 bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 leading-relaxed">{ticket.work_description}</p>
                </RSSection>
              )}
              {ticket.parts_used?.length > 0 && (
                <RSSection title="Refacciones" count={ticket.parts_used.length} icon={<Tag size={13} />}>
                  {ticket.parts_used.map((p, i) => (
                    <div key={`part-${i}`} className="flex items-center gap-2 text-xs py-1.5 border-b last:border-0">
                      <span className="bg-slate-100 rounded px-1.5 py-0.5 font-mono text-[10px] font-bold">{p.qty}x</span>
                      {p.code && <span className="text-blue-600 font-mono text-[10px]">{p.code}</span>}
                      <span className="text-slate-600 flex-1 truncate">{p.description}</span>
                    </div>
                  ))}
                </RSSection>
              )}
            </>
          )}

          {/* Ticket Times */}
          <RSSection title="Tiempos" count={1} icon={<Clock size={13} />}>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Creado</span><span className="font-semibold">{format(new Date(ticket.created_at), "dd MMM HH:mm", { locale: es })}</span></div>
              {ticket.closed_at && <div className="flex justify-between"><span className="text-slate-500">Cerrado</span><span className="font-semibold text-emerald-600">{format(new Date(ticket.closed_at), "dd MMM HH:mm", { locale: es })}</span></div>}
              {ticket.resolution_time && <div className="flex justify-between"><span className="text-slate-500">Resolucion</span><span className="font-bold text-amber-600">{ticket.resolution_time}</span></div>}
            </div>
          </RSSection>
        </div>
      </div>
      {/* Lightbox */}
      {lightboxImages.length > 0 && lightboxImages[lightboxIndex] && (
        <ImageLightbox
          images={lightboxImages.filter(Boolean)}
          startIndex={Math.min(lightboxIndex, lightboxImages.filter(Boolean).length - 1)}
          onClose={() => { setLightboxImages([]); setLightboxIndex(0); }}
        />
      )}
    </div>
  );
};

// Helper components for ticket detail
function Field({ label, value, blue, amber, green }) {
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-xs font-medium mt-0.5 ${blue ? "text-blue-600" : amber ? "text-amber-600 font-bold" : green ? "text-emerald-600 font-bold" : "text-slate-700"}`}>{value}</p>
    </div>
  );
}

function RSSection({ title, count, icon, children }) {
  return (
    <div className="border-b">
      <div className="flex items-center justify-between px-3 py-2.5">
        <p className="text-[11px] font-semibold text-slate-800 flex items-center gap-1.5">{icon} {title} <span className="text-slate-400 font-normal">({count})</span></p>
      </div>
      {children && <div className="px-3 pb-2.5">{children}</div>}
    </div>
  );
}

function ProductSearchForTicket({ token, currentValue, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  const search = async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    try {
      const { data } = await axios.get(`${API}/products?search=${encodeURIComponent(q)}&limit=5`, { headers: { Authorization: `Bearer ${token}` } });
      setResults(data.products || []);
    } catch { setResults([]); }
  };

  return (
    <div className="relative">
      <input value={query} onChange={e => { setQuery(e.target.value); setOpen(true); clearTimeout(debounceRef.current); debounceRef.current = setTimeout(() => search(e.target.value), 300); }}
        placeholder="Buscar producto o escribir manual..." className="w-full px-2.5 py-1.5 border rounded-lg text-[11px] outline-none focus:border-blue-400" />
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-[200px] overflow-y-auto">
          {results.map(p => (
            <button key={p.id} onClick={() => { onSelect(p.name); setQuery(""); setOpen(false); setResults([]); }}
              className="w-full text-left px-2.5 py-2 hover:bg-blue-50 border-b last:border-0 text-[11px]">
              <p className="font-semibold text-slate-800">{p.name}</p>
              <p className="text-[9px] text-slate-400"><span className="text-blue-600 font-mono">{p.sku}</span> · {p.stock_qty > 0 ? `${p.stock_qty} en stock` : "Sin stock"}</p>
            </button>
          ))}
        </div>
      )}
      {query.length >= 2 && results.length === 0 && open && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-xl p-2">
          <button onClick={() => { onSelect(query); setQuery(""); setOpen(false); }} className="w-full text-left text-[11px] text-blue-600 hover:underline">Usar "{query}" como texto manual</button>
        </div>
      )}
    </div>
  );
}

const EvidenceImage = ({ fileId, token, onClick }) => {
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const response = await axios.get(`${API}/files/${fileId}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        });
        const blobUrl = URL.createObjectURL(response.data);
        setImageSrc(blobUrl);
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error("Error fetching image:", error);
      }
    };
    fetchImage();
    
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [fileId, token]);

  if (!imageSrc) {
    return <div className="w-full h-full bg-slate-100 animate-pulse" />;
  }

  return (
    <div className="w-full h-full relative group cursor-pointer" onClick={() => onClick?.(imageSrc)}>
      <img src={imageSrc} alt="Evidencia" className="w-full h-full object-cover" />
      {onClick && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <MagnifyingGlass size={20} weight="bold" className="text-white opacity-0 group-hover:opacity-100 drop-shadow-lg" />
        </div>
      )}
    </div>
  );
};

// ===== LIGHTBOX COMPONENT =====
const ImageLightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex || 0);
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx(i => Math.min(i + 1, images.length - 1));
      if (e.key === "ArrowLeft") setIdx(i => Math.max(i - 1, 0));
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", handleKey); document.body.style.overflow = ""; };
  }, [images.length, onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" data-testid="image-lightbox" onClick={onClose}>
      <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/20 backdrop-blur text-white text-xs font-bold z-10">
        {idx + 1} / {images.length}
      </div>
      <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white hover:bg-white/30 z-10" data-testid="lightbox-close">
        <X size={18} weight="bold" />
      </button>
      {images.length > 1 && idx > 0 && (
        <button onClick={(e) => { e.stopPropagation(); setIdx(idx - 1); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white hover:bg-white/30 z-10" data-testid="lightbox-prev">
          <CaretRight size={16} weight="bold" className="rotate-180" />
        </button>
      )}
      {images.length > 1 && idx < images.length - 1 && (
        <button onClick={(e) => { e.stopPropagation(); setIdx(idx + 1); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white hover:bg-white/30 z-10" data-testid="lightbox-next">
          <CaretRight size={16} weight="bold" />
        </button>
      )}
      <img src={images[idx]} alt={`Evidencia ${idx + 1}`} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
    </div>
  );
};


export { StatusBadge, AlertIndicator, TicketsList, NewTicketForm, TicketDetail, EvidenceImage, ImageLightbox };
